import type { Alpine as AlpineAPI } from 'alpinejs';
import markup from '../../modules/multi-select-control.template.html' with { type: 'text' };

type Id = string | number;
type Item = { id: Id; name: string; group?: string; disabled?: boolean };
type Items = Item[] | Record<string, Item>;
type Config = {
    items?: Items | (() => Items);
    selected?: Id[];
    name?: string;
    placeholder?: string;
    native?: boolean;
    onChange?: (selected: Id[]) => void;
};
const enhanced = new WeakSet<HTMLSelectElement>();

/** One picker for PHP-rendered filters and legacy selects; submission formats stay unchanged. */
export function registerMultiselect(Alpine: AlpineAPI) {
    Alpine.data('searchableMultiSelect', (config: Config = {}) => ({
        items: config.items || [] as Items,
        selected: Array.isArray(config.selected) ? [...config.selected] : [] as Id[],
        name: config.name || '',
        placeholder: config.placeholder || 'Select items…',
        searchTerm: '',
        isOpen: false,
        disabled: false,
        native: null as HTMLSelectElement | null,
        init() {
            if (config.native && this.$el.previousElementSibling instanceof HTMLSelectElement) {
                this.native = this.$el.previousElementSibling;
                this.items = [...this.native.options].map(option => ({
                    id: option.value, name: option.text,
                    group: option.parentElement instanceof HTMLOptGroupElement ? option.parentElement.label : undefined,
                    disabled: option.disabled || (option.parentElement instanceof HTMLOptGroupElement && option.parentElement.disabled),
                }));
                this.selected = [...this.native.options].filter(option => option.hasAttribute('selected') || option.dataset.selected === 'true').map(option => option.value);
                this.placeholder = this.native.dataset.label || 'Select items…';
                this.disabled = this.native.disabled;
                this.syncNative(false);
                if (this.native.classList.contains('auto-open')) this.$nextTick(() => this.open());
            }
            this.$watch('selected', () => {
                this.syncNative(true);
                config.onChange?.([...this.selected]);
            });
        },
        allItems(): Item[] {
            const items = typeof this.items === 'function' ? this.items() : this.items;
            return Array.isArray(items) ? items : Object.values(items || {});
        },
        updateItems(items: Items) {
            this.items = items || [];
            const available = new Set(this.allItems().map(item => String(item.id)));
            const selected = this.selected.filter(id => available.has(String(id)));
            if (selected.length !== this.selected.length) this.selected = selected;
        },
        filteredItems(): Item[] {
            const term = this.searchTerm.trim().toLocaleLowerCase();
            return this.allItems().filter(item => !term || `${item.name} ${item.group || ''}`.toLocaleLowerCase().includes(term));
        },
        groupedFilteredItems(): Record<string, Item[]> {
            const groups: Record<string, Item[]> = Object.create(null);
            for (const item of this.filteredItems()) (groups[item.group || 'Options'] ||= []).push(item);
            return groups;
        },
        isSelected(id: Id): boolean { return this.selected.some(value => String(value) === String(id)); },
        summaryText(): string {
            const names = this.allItems().filter(item => this.isSelected(item.id)).map(item => item.name);
            return names.slice(0, 2).join(', ') + (names.length > 2 ? ` + ${names.length - 2} more` : '');
        },
        open() {
            if (this.disabled) return;
            this.isOpen = true;
            this.searchTerm = '';
            // x-show applies visibility on the next frame in WebKit.
            this.$nextTick(() => requestAnimationFrame(() => { if (this.isOpen) this.$refs.search?.focus(); }));
        },
        toggle() { if (this.isOpen) this.close(); else this.open(); },
        close(restoreFocus = false) {
            this.isOpen = false;
            if (restoreFocus) this.$refs.trigger?.focus();
        },
        focusOption(index: number) {
            this.$refs.options?.querySelectorAll<HTMLInputElement>('input:not(:disabled)')[index]?.focus();
        },
        moveOption(direction: number) {
            const options = [...(this.$refs.options?.querySelectorAll<HTMLInputElement>('input:not(:disabled)') || [])];
            const index = options.indexOf(document.activeElement as HTMLInputElement);
            options[(index + direction + options.length) % options.length]?.focus();
        },
        selectAllFiltered() {
            const selected = [...this.selected];
            for (const item of this.filteredItems()) if (!item.disabled && !this.isSelected(item.id)) selected.push(item.id);
            this.selected = selected;
        },
        deselectAll() { this.selected = this.selected.filter(id => this.allItems().some(item => String(item.id) === String(id) && item.disabled)); },
        syncNative(notify: boolean) {
            if (!this.native) return;
            this.native.multiple = true;
            for (const option of this.native.options) option.selected = this.isSelected(option.value);
            const target = document.getElementById(this.native.dataset.live || '');
            if (target instanceof HTMLInputElement) {
                target.value = this.selected.join(',');
                if (notify) target.dispatchEvent(new Event('change', { bubbles: true }));
            }
        },
    }));
}

export function enhanceMultiselects(root: ParentNode) {
    const selects = [...(root instanceof HTMLSelectElement && root.matches('.select-multi') ? [root] : []), ...root.querySelectorAll<HTMLSelectElement>('select.select-multi')];
    for (const select of selects) {
        if (enhanced.has(select)) continue;
        enhanced.add(select);
        select.hidden = true;
        const wrapper = document.createElement('div');
        wrapper.className = 'mb-4';
        wrapper.setAttribute('x-data', 'searchableMultiSelect({native: true})');
        wrapper.innerHTML = markup;
        select.after(wrapper);
    }
}
